import { useCallback, useEffect, useMemo } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useBoardsUiStore } from '../../extensions/bundled/task-manager/boardsUiStore';
import teamsService from '../../services/teamsService';
import { effectiveCreatedBy } from '../../extensions/bundled/task-manager/boardCreatedBy';
import { loadTeamBoardOwners } from '../../extensions/bundled/task-manager/teamBoardOwners';

/**
 * Who owns each board on the overview (Boards Redesign extras §2.8): the
 * avatar bottom-right of a tile.
 *
 * - A private board (personal or this-device scope) is the signed-in user's.
 * - A team board names its creator in `board.createdBy` (written on create
 *   since 1.0.7). Your own id needs no lookup; anyone else is looked up in the
 *   cached team roster (`boardsUiStore.teamMembersByTeam`, the same cache the
 *   assignee picker fills), fetched here at most once per team and only when a
 *   board actually needs it. Missing or unknown → no avatar, never a guess.
 * - A team board made before 1.0.7 has no `createdBy`: the server's answer
 *   (`created_by` of GET /teams/:id/boards, loaded once per such team) stands
 *   in until the open board writes it back (useBoardCreatedByFill).
 *
 * Returns `ownerFor(board) → { id, name } | null`.
 */
export default function useBoardOwners(boards) {
  const me = useAuthStore((s) => s.user);
  const membersByTeam = useBoardsUiStore((s) => s.teamMembersByTeam);
  const setTeamMembers = useBoardsUiStore((s) => s.setTeamMembers);
  const teamBoardOwners = useBoardsUiStore((s) => s.teamBoardOwners);
  const myId = me?.id != null ? String(me.id) : null;
  const creatorOf = useCallback(
    (b) => effectiveCreatedBy(b, { myId, teamOwners: teamBoardOwners[String(b?.teamId)] }),
    [myId, teamBoardOwners]
  );

  // Teams with a board that has no `createdBy` yet: ask the server who made it.
  const ownerlessTeams = useMemo(() => {
    const out = new Set();
    for (const b of boards || []) {
      if (b?.visibility === 'team' && b.teamId != null && b.createdBy == null) out.add(String(b.teamId));
    }
    return [...out].sort().join(',');
  }, [boards]);
  useEffect(() => {
    if (ownerlessTeams) ownerlessTeams.split(',').forEach((teamId) => loadTeamBoardOwners(teamId));
  }, [ownerlessTeams]);

  // Teams whose roster a tile needs and that is not cached yet.
  const missingTeams = useMemo(() => {
    const out = new Set();
    for (const b of boards || []) {
      if (b?.visibility !== 'team' || b.teamId == null) continue;
      const creator = creatorOf(b);
      if (creator == null || creator === myId) continue;
      if (!membersByTeam[String(b.teamId)]) out.add(String(b.teamId));
    }
    return [...out].sort();
  }, [boards, membersByTeam, myId, creatorOf]);
  const missingKey = missingTeams.join(',');

  useEffect(() => {
    if (!missingKey) return undefined;
    const state = { cancelled: false };
    const fetchRoster = (teamId) =>
      teamsService
        .getTeamMembers(teamId)
        .then((res) => {
          if (!state.cancelled) setTeamMembers(teamId, Array.isArray(res) ? res : res?.data || []);
        })
        .catch(() => {
          // No roster → those tiles show no owner; nothing else depends on it.
        });
    missingKey.split(',').forEach(fetchRoster);
    return () => {
      state.cancelled = true;
    };
  }, [missingKey, setTeamMembers]);

  return useCallback(
    (board) => {
      if (!board) return null;
      if (board.visibility !== 'team') return myId ? { id: myId, name: me?.name || me?.email || '' } : null;
      const id = creatorOf(board);
      if (id == null) return null;
      if (id === myId) return { id, name: me?.name || me?.email || '' };
      const member = (membersByTeam[String(board.teamId)] || []).find((m) => m && String(m.id) === id);
      return member ? { id, name: member.name || member.email || '' } : null;
    },
    [membersByTeam, me, myId, creatorOf]
  );
}
